import React, { FC, useState } from "react";
import dashStyles from "../Dashboard/Dashboard.module.scss";
import SelfAttendance, { EventAttendance } from "./SelfAttendance";
import MarkAttendance from "../Admin/MarkAttendance";
import AttendanceSummary from "./AttendanceSummary";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";

interface AttendanceProps {
  /** The user's own attendance records (for the "My Attendance" view). */
  data: EventAttendance[];
  /** True when Mark Attendance is available (active practice + permission). */
  canMark: boolean;
  /** True when the user has mark_overall_attendance permission (Summary is always available to them). */
  canViewSummary?: boolean;
  /** Reports the open subtile's label for the dashboard breadcrumb (null = grid). */
  onSubSectionChange?: (label: string | null) => void;
}

type View = "mark-attendance" | "my-attendance" | "summary";

const Attendance: FC<AttendanceProps> = ({ data, canMark, canViewSummary = false, onSubSectionChange }) => {
  const [view, setView] = useState<View | null>(null);

  // No elevated permissions at all → show the user's own attendance directly.
  if (!canMark && !canViewSummary) {
    return <SelfAttendance data={data} />;
  }

  const openView = (next: View, label: string) => {
    setView(next);
    onSubSectionChange?.(label);
  };

  if (view === "mark-attendance") return <MarkAttendance />;
  if (view === "my-attendance")   return <SelfAttendance data={data} />;
  if (view === "summary")         return <AttendanceSummary />;

  // Present subtiles based on what the user is allowed to see.
  return (
    <section className="d-flex gap-5 mx-5 my-5 flex-wrap">
      {canMark && (
        <div
          className={dashStyles.Dashboard__tab}
          onClick={() => openView("mark-attendance", "Mark Attendance")}
        >
          <HowToRegOutlinedIcon className={dashStyles.Dashboard__tab_icon} />
          Mark Attendance
        </div>
      )}
      <div
        className={dashStyles.Dashboard__tab}
        onClick={() => openView("my-attendance", "My Attendance")}
      >
        <FactCheckOutlinedIcon className={dashStyles.Dashboard__tab_icon} />
        My Attendance
      </div>
      {canViewSummary && (
        <div
          className={dashStyles.Dashboard__tab}
          onClick={() => openView("summary", "Summary")}
        >
          <AssessmentOutlinedIcon className={dashStyles.Dashboard__tab_icon} />
          Summary
        </div>
      )}
    </section>
  );
};

export default Attendance;
